<template>
  <div class="home-banner" :class="[deviceType]">
    <img src="@/assets/baby.png" alt="baby" class="banner-image" />
    <div class="banner-content">
      <router-link to="/names" class="banner-text-link">
        <div class="banner-text">
          <h1 class="banner-title">Discover the Perfect Baby Name</h1>
          <p class="banner-description">Smart, simple, and culturally rich name ideas — powered by
            AI.
          </p>
        </div>
      </router-link>
    </div>
    <div class="banner-cards">
      <template v-for="(card, index) in nameCards.slice(0, 4)">
        <router-link :key="index" :to="`/names/${card.name.toLowerCase()}/${card.id}`" tag="div"
          class="name-card" @mouseenter="setHoveredCard(index)" @mouseleave="setHoveredCard(null)"
          :class="{ 'hovered': hoveredCard === index, 'animated': true, 'animation-complete': animationsCompleted }"
          :style="{ 'animation-delay': `${index * 0.2}s`, background: card.background }">
          <div class="card-header">
            <div class="card-info">
              <div class="card-name">{{ card.name }}</div>
              <div class="card-pronunciation">{{ card.pronunciation }}</div>
            </div>
            <div class="card-gender-icon">
              <img :src="card.genderIcon" alt="gender" />
            </div>
          </div>

          <div class="card-details">
            <div class="detail-row">
              <span class="detail-label">US Popularity</span>
              <span class="detail-value">{{ card.popularity }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Style</span>
              <span class="detail-value">{{ card.style }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Meaning</span>
              <span class="detail-value text-ellipsis">
                <span v-for="item in card.meaningList" :key="item">{{ item }} </span></span>
            </div>
          </div>
        </router-link>

      </template>
    </div>

    <div class="banner-actions">
      <div class="generate-button" @mouseenter="isButtonHovered = true"
        @mouseleave="isButtonHovered = false" :class="{ 'hovered': isButtonHovered }"
        @click="goToNamesPage">
        <div class="button-icon">
          <img src="@/assets/icons/generate_white.svg" alt="icon" />
        </div>
        <div class="button-text">Generate Names</div>
      </div>
      <div class="card-icon" @click="fetchRandomNames()">
        <img src="@/assets/icons/refresh.svg" alt="icon" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import AdComponent from '@/pages/components/AdComponent.vue'

export default {
  name: 'HomeBanner',
  components: {
    AdComponent
  },
  data () {
    return {
      isButtonHovered: false,
      hoveredCard: null,
      animationsCompleted: false,
      nameCards: [],
      randomOptions: [
        { gender: ['Boy'] },
        { gender: ['Girl'] },
        { gender: ['Unisex'] },
        { syllables: '2' },
        { syllables: '3' }
      ],
      filterTotals: {} // To cache total counts for each filter
    };
  },
  computed: {
    ...mapGetters([
      'deviceType',
      'gender',
      'style',
      'meaning',
      'domainConfig',
      'showDebug',
      'adsensConfig'
    ])
  },
  mounted () {
    this.fetchRandomNames();
  },
  methods: {
    async fetchRandomNames () {
      this.animationsCompleted = false;
      this.nameCards = [];
      await this.$nextTick();
      try {
        // 1. From 5 predefined options, randomly choose one
        const randomIndex = Math.floor(Math.random() * this.randomOptions.length);
        const randomFilter = this.randomOptions[randomIndex];
        const filterKey = JSON.stringify(randomFilter);

        // 2. Prepare base request parameters
        let params = {
          pageSize: '4',
          pageNum: '1',
          sortType: 'S_3', // Default sort by popularity
          ...randomFilter
        };

        // 3. Use cached total to determine page number for more variety
        const storedTotal = this.filterTotals[filterKey];
        if (storedTotal && storedTotal > 4) {
          const totalPages = Math.ceil(storedTotal / 4);
          if (totalPages > 1) {
            // Pick a random page, but not the last one, to increase variety
            params.pageNum = Math.floor(Math.random() * (totalPages - 1)) + 1;
          }
        }

        // Add origin from domainConfig
        if (this.domainConfig && this.domainConfig.origin) {
          params.origin = this.domainConfig.origin;
        }

        // 4. Make a single API request
        const response = await this.$global.server.postAPI({
          method: 'post',
          params: params,
          url: '/name/page',
        });

        if (response.data) {
          // 5. Cache the new total count
          this.filterTotals[filterKey] = response.data.total || 0;

          // 6. Update the name cards for display
          const list = response.data.list || [];
          if (list.length > 0) {
            const GENDER_COLORS = {
              Boy: 'linear-gradient(180deg, rgba(0, 136, 255, 1) 0%, rgba(0, 136, 255, 0.2) 100%)',
              Girl: 'linear-gradient(180deg, rgba(229, 76, 186, 1) 0%, rgba(229, 76, 186, 0.2) 100%)',
              Unisex: 'linear-gradient(180deg, rgba(200, 173, 23, 1) 0%, rgba(200, 173, 23, 0.2) 100%)'
            };
            this.nameCards = list.slice(0, 4).map(item => ({
              id: item.id || '',
              name: item.name || 'N/A',
              genderIcon: item.gender === 'Boy'
                ? require('@/assets/icons/male.svg')
                : item.gender === 'Unisex'
                  ? require('@/assets/icons/unsex.svg')
                  : require('@/assets/icons/female.svg'),
              pronunciation: item.pronunciation || 'N/A',
              style: item.style || 'N/A',
              meaning: item.meaning || 'N/A',
              meaningList: item.meaningList || [],
              popularity: item.popularity || 'N/A',
              background: GENDER_COLORS[item.gender] || GENDER_COLORS['Boy']
            }));
            console.log('Banner random names fetched successfully:', this.nameCards);
            setTimeout(() => {
              this.animationsCompleted = true;
            }, 1500);
          } else if (this.nameCards.length === 0) {
            console.log('No names found for the selected filter.');
          }
        }
      } catch (error) {
        console.error('Error fetching random names for banner:', error);
      }
    },
    setHoveredCard (index) {
      if (this.animationsCompleted) {
        this.hoveredCard = index;
      }
    },
    goToNamesPage () {
      this.$router.push('/names');
    }
  }
};
</script>
    <style lang="scss" scoped>
@import '~@/css/variables.scss';

.home-banner {
	background: #000000;
	padding: rem(80) rem(180) rem(40) rem(180);
	display: flex;
	flex-direction: column;
	gap: rem(40);
	align-items: flex-start;
	justify-content: flex-start;
	position: relative;

	.banner-image {
		width: rem(644);
		height: rem(430);
		position: absolute;
		right: 0;
		top: 0;
		z-index: 1;
	}

	.banner-content {
		display: flex;
		flex-direction: column;
		gap: rem(16);
		align-items: flex-start;
		justify-content: flex-start;
		align-self: stretch;

		.banner-text-link {
			text-decoration: none;
			cursor: pointer;
		}

		.banner-text {
			.banner-title {
				color: #ffffff;
				font-size: rem(52);
				line-height: rem(60);
				font-weight: 900;
			}

			.banner-description {
				color: #ffffff;
				font-size: rem(20);
				line-height: rem(28);
				font-weight: 400;
			}
		}
	}

	.banner-cards {
		display: flex;
		flex-direction: row;
		align-items: flex-start;
		justify-content: space-between;
		align-self: stretch;
		position: relative;
		z-index: 2;

		a {
			text-decoration: none;
		}

		.name-card {
			border-radius: rem(16);
			padding: rem(16);
			display: flex;
			flex-direction: column;
			gap: rem(16);
			width: rem(252);
			box-sizing: border-box;
			cursor: pointer;
			opacity: 0;
			transition: all 0.3s ease;

			&:hover {
				transform: translateY(rem(-4));
			}

			&.animated {
				animation: cardFadeIn 0.5s forwards;
			}

			&.animation-complete {
				transition: background 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;
			}
			.card-header {
				display: flex;
				flex-direction: row;
				justify-content: space-between;
				align-self: stretch;

				.card-info {
					display: flex;
					flex-direction: column;
					gap: rem(8);

					.card-name {
						color: #ffffff;
						font-size: rem(20);
						line-height: rem(20);
						font-weight: 900;
					}

					.card-pronunciation {
						color: #ffffff;
						font-size: rem(12);
						line-height: rem(12);
						font-weight: 300;
						font-style: italic;
					}
				}

				.card-gender-icon {
					width: rem(24);
					height: rem(24);
					flex-direction: column;
					display: flex;
					justify-content: center;
					align-items: center;
					gap: 10px;
					flex-shrink: 0;
					aspect-ratio: 1/1;
					border-radius: rem(8);
					background: #fff;

					img {
						width: rem(16);
						height: rem(16);
					}
				}
			}

			.card-details {
				display: flex;
				flex-direction: column;
				gap: rem(8);
				align-self: stretch;

				.detail-row {
					background: rgba(255, 255, 255, 0.1);
					border-radius: rem(8);
					padding: rem(8) rem(12);
					display: flex;
					justify-content: space-between;
					align-items: center;
					height: rem(32);
					box-sizing: border-box;

					.detail-label {
						color: #ffffff;
						font-size: rem(12);
						line-height: rem(12);
						font-weight: 300;
					}

					.detail-value {
						color: #ffffff;
						font-size: rem(13);
						line-height: rem(14);
						font-weight: 700;

						&.text-ellipsis {
							white-space: nowrap;
							overflow: hidden;
							text-overflow: ellipsis;
							width: 50%;
							text-align: right;
						}
					}
				}
			}
		}
	}

	.banner-actions {
		display: flex;
		flex-direction: row;
		gap: rem(24);
		align-items: center;
		justify-content: flex-start;
		align-self: stretch;

		.generate-button {
			background: linear-gradient(92.87deg, rgba(0, 136, 255, 1) 0%, rgba(229, 76, 186, 1) 50%, rgba(200, 173, 23, 1) 100%);
			border-radius: rem(16);
			padding: rem(20) rem(32);
			display: flex;
			flex-direction: row;
			gap: rem(20);
			align-items: center;
			justify-content: center;
			flex: 1;
			height: rem(52);
			box-sizing: border-box;
			box-shadow: 0 rem(2) rem(4) 0 rgba(0, 0, 0, 0.08);
			cursor: pointer;
			transition: all 0.3s ease;
			position: relative;

			&:hover {
				&::before {
					content: '';
					position: absolute;
					top: 0;
					left: 0;
					right: 0;
					bottom: 0;
					background: rgba(0, 0, 0, 0.3);
					border-radius: rem(16);
				}
			}

			.button-icon {
				width: rem(24);
				height: rem(24);
				position: relative;
				z-index: 1;

				img {
					width: 100%;
					height: 100%;
				}
			}

			.button-text {
				color: #ffffff;
				font-size: rem(20);
				line-height: rem(20);
				font-weight: 900;
				position: relative;
				z-index: 1;
			}
		}

		.card-icon {
			background: #ffffff;
			border-radius: rem(16);
			padding: rem(12);
			width: rem(52);
			height: rem(52);
			box-sizing: border-box;
			cursor: pointer;
			transition: all 0.3s ease;
			display: flex;
			align-items: center;
			justify-content: center;

			&:hover {
				background: rgba(255, 255, 255, 0.7);
			}

			img {
				width: rem(24);
				height: rem(24);
			}
		}
	}
}

@keyframes cardFadeIn {
	0% {
		opacity: 0;
		transform: translateY(rem(20));
	}
	100% {
		opacity: 1;
		transform: translateY(0);
	}
}
.detect_deviece_mobile {
	.home-banner {
		padding: rem(40) rem(16);
		background: url('../assets/baby-m.png') no-repeat;
		background-position: top right;

		.banner-image {
			display: none;
		}

		.banner-content {
			.banner-text {
				.banner-title {
					font-size: rem(40);
					line-height: rem(48);
				}

				.banner-description {
					font-size: rem(16);
					line-height: rem(24);
				}
			}
		}

		.banner-cards {
			flex-direction: row;
			gap: rem(16);
			overflow-x: auto;
			margin: 0 rem(-16);
			padding-left: rem(16);
			padding-right: rem(16);

			// Hide scrollbar
			-ms-overflow-style: none;
			/* IE and Edge */
			scrollbar-width: none;
			/* Firefox */
			&::-webkit-scrollbar {
				display: none;
			}

			.name-card {
				width: rem(252);
				flex-shrink: 0;
			}
		}

		.banner-actions {
			.generate-button {
				.button-text {
					font-size: rem(18);
				}
			}
		}
	}
}
</style>